/**
 * File download utilities with React Native WebView bridge support.
 *
 * When the app runs inside a React Native WebView, blobs cannot be passed
 * across the bridge. Instead we send a `downloadRequest` message to the
 * native shell with the URL, method, headers, and filename so the native
 * side can perform the download itself.
 *
 * In a regular browser the file is saved via the `file-saver` library.
 */

import { AxiosResponse } from 'axios';
import { saveAs } from 'file-saver';
import { isReactNativeWebView, postMessageToNative } from '@/lib/webview-utils';

// ---------------------------------------------------------------------------
// Header / MIME helpers
// ---------------------------------------------------------------------------

const CONTENT_DISPOSITION_FILENAME_REGEX = /[\d\w\-+.:/ ]+$/;

/**
 * Extracts the filename from the `Content-Disposition` header.
 * Falls back to `defaultFileName` when the header is missing or unparseable.
 */
export function getFileNameFromDispositionHeader(
  axiosResponse: AxiosResponse,
  defaultFileName: string
): string {
  const header = axiosResponse.headers['content-disposition'] || '';
  if (CONTENT_DISPOSITION_FILENAME_REGEX.test(header)) {
    return CONTENT_DISPOSITION_FILENAME_REGEX.exec(header)![0];
  }
  return defaultFileName;
}

/**
 * Returns `true` when running on iOS Safari (not Chrome on iOS).
 * Used to work around iOS Safari PDF rendering quirks.
 */
export function checkIfIosSafari(): boolean {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(ua);
  const isWebkit = /WebKit/i.test(ua);
  return isIos && isWebkit && !/CriOS/i.test(ua);
}

/**
 * Determines the MIME content type from the `Content-Disposition` header's
 * file extension. On iOS Safari, PDFs are returned as `application/octet-stream`
 * to force a download instead of in-browser rendering.
 */
export function getContentType(axiosResponse: AxiosResponse): string {
  const header = axiosResponse.headers['content-disposition'] || '';
  const iOSSafari = checkIfIosSafari();

  if (CONTENT_DISPOSITION_FILENAME_REGEX.test(header)) {
    const name = CONTENT_DISPOSITION_FILENAME_REGEX.exec(header)![0];
    const extension = name.slice(name.lastIndexOf('.') + 1).toLowerCase();

    const MIME_MAP: Record<string, string> = {
      pdf: iOSSafari ? 'application/octet-stream' : 'application/pdf',
      csv: 'text/csv',
      xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      qwc: 'application/xml',
      pkpass: 'application/vnd.apple.pkpass',
      zip: 'application/zip',
    };

    return MIME_MAP[extension] ?? 'application/json';
  }

  return 'text/plain';
}

// ---------------------------------------------------------------------------
// Simple blob download
// ---------------------------------------------------------------------------

/**
 * Triggers a file download from raw blob data.
 * Use this when you already have the file data (e.g. from a service that
 * returns `responseType: 'blob'`) and just need to save it to disk.
 */
export function downloadBlob(data: BlobPart, filename: string, type = 'application/pdf'): void {
  const blob = new Blob([data], { type });
  saveAs(blob, filename);
}

// ---------------------------------------------------------------------------
// Download entry-point
// ---------------------------------------------------------------------------

/**
 * Saves a file from an Axios response.
 *
 * - **React Native WebView**: sends a `downloadRequest` postMessage to the
 *   native shell (blobs cannot cross the bridge).
 * - **Browser**: creates a Blob and triggers a download via `file-saver`.
 */
export function downloadFileFromResponse(axiosResponse: AxiosResponse): void {
  const contentType = getContentType(axiosResponse);
  const fileName = getFileNameFromDispositionHeader(axiosResponse, 'report.pdf');

  if (isReactNativeWebView()) {
    postMessageToNative({
      type: 'downloadRequest',
      url: axiosResponse.request?.responseURL || '',
      method: axiosResponse.config.method?.toUpperCase() || 'GET',
      headers: axiosResponse.config?.headers || {},
      filename: fileName,
    });
    return;
  }

  const blob = new Blob([axiosResponse.data], { type: contentType });
  saveAs(blob, fileName);
}
